import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, shareReplay, Subject, tap } from 'rxjs';
import { EntityPagesTitle } from '../app-pages-title/app-pages-title';
import { SessionService } from '../auth/session.service';
import { TranslateParams } from '../interfaces';
import { NotificationService } from '../notifications/notifications.service';
import { MaybeUndefined } from '../utils';

export abstract class RouteService<TEntity, ETitle> {
  protected router = inject(Router);
  protected destroyRef = inject(DestroyRef);
  protected sessionService = inject(SessionService);
  protected informService = inject(NotificationService);
  protected translateService = inject(TranslateService);

  protected dataLoaded$: Subject<TEntity> = new Subject<TEntity>();

  public readonly data$: Observable<TEntity> = this.dataLoaded$.pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public abstract loadData(entityId: string, queryParams?: object): void;
  public abstract verifyAccess(data: TEntity): void;
  public abstract buildTitle(data: TEntity): ETitle;

  public subscribeToData(observable: Observable<TEntity>): void {
    observable
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((data) => {
          this.dataLoaded$.next(data);
        }),
      )
      .subscribe();
  }

  public translate(key: string | Array<string>, interpolateParams?: TranslateParams): string {
    return this.translateService.instant(key, interpolateParams) as string;
  }
}

export const viewEntityResolver: <TEntity>() => ResolveFn<TEntity> = <TEntity>() => {
  return (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
    const entityId: MaybeUndefined<string> = route.paramMap.get('id');

    if (!entityId) {
      console.error('entity id should be provided');
      return EMPTY;
    }

    const service: RouteService<TEntity, EntityPagesTitle> = inject(RouteService<TEntity, EntityPagesTitle>);

    service.loadData(entityId, route.queryParams);

    return service.data$.pipe(
      tap((data: TEntity) => {
        route.routeConfig.title = service.buildTitle(data).viewPage;
      }),
    );
  };
};

export const editEntityResolver: <TEntity>() => ResolveFn<TEntity> = <TEntity>() => {
  return (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
    const entityId: MaybeUndefined<string> = route.paramMap.get('id');

    if (!entityId) {
      console.error('entity id should be provided');
      return EMPTY;
    }

    const service: RouteService<TEntity, EntityPagesTitle> = inject(RouteService<TEntity, EntityPagesTitle>);

    service.loadData(entityId);

    return service.data$.pipe(
      tap((data: TEntity) => {
        service.verifyAccess(data);
        route.routeConfig.title = service.buildTitle(data).editPage;
      }),
    );
  };
};
